import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, DollarSign, FileText, PenTool, Loader2 } from 'lucide-react';

const PublicQuoteView = () => {
  const { quoteId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [prospect, setProspect] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchQuote();
  }, [quoteId, token]);

  const fetchQuote = async () => {
    try {
      // Public endpoint - no auth required
      const response = await axios.get(`${API_URL}/api/public/quotes/${quoteId}/${token}`);
      setQuote(response.data.quote);
      setProspect(response.data.prospect);
      
      // If quote is accepted, fetch contracts
      if (response.data.quote.status === 'accepted') {
        fetchContracts();
      }
    } catch (error) {
      setError('Quote not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    setLoadingContracts(true);
    try {
      // We need a public endpoint to get contracts, or use the internal one without auth
      const response = await axios.get(`${API_URL}/api/public/quotes/${quoteId}/contracts`);
      setContracts(response.data.contracts || []);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    } finally {
      setLoadingContracts(false);
    }
  };

  const handleAcceptQuote = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API_URL}/api/public/quotes/${quoteId}/${token}/accept`);
      toast.success('Quote accepted! Generating contracts...');
      await fetchQuote(); // Refresh to update status
      
      // Wait a moment then fetch contracts
      setTimeout(() => {
        fetchContracts();
      }, 2000);
    } catch (error) {
      toast.error('Failed to accept quote. Please try again or contact us.');
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectQuote = async () => {
    setRejecting(true);
    try {
      await axios.post(`${API_URL}/api/public/quotes/${quoteId}/${token}/reject`);
      toast.success('Thank you for your feedback. We will review your requirements.');
      fetchQuote(); // Refresh to update status
    } catch (error) {
      toast.error('Failed to process your response. Please contact us directly.');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <DollarSign className="w-8 h-8 text-emerald-600 animate-pulse" />
          </div>
          <p className="text-slate-600">Loading your quote...</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Quote Not Found</h2>
          <p className="text-slate-600 mb-6">
            {error || 'This quote link may have expired or is invalid.'}
          </p>
          <p className="text-sm text-slate-500">
            If you believe this is an error, please contact us directly.
          </p>
        </div>
      </div>
    );
  }

  const isExpired = quote.valid_until && new Date(quote.valid_until) < new Date();
  const canTakeAction = quote.status === 'sent' && !isExpired;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Your Service Quote</h1>
            <p className="text-slate-600">Quote #{quote.quote_number}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Banner */}
        {quote.status === 'accepted' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-green-900">Quote Accepted</p>
              <p className="text-sm text-green-700">Thank you! We will be in touch shortly to get started.</p>
            </div>
          </div>
        )}

        {quote.status === 'rejected' && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-slate-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-900">Quote Declined</p>
              <p className="text-sm text-slate-700">Thank you for your response. Please contact us to discuss alternatives.</p>
            </div>
          </div>
        )}

        {isExpired && quote.status === 'sent' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
            <Clock className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-900">Quote Expired</p>
              <p className="text-sm text-amber-700">This quote has expired. Please contact us for an updated quote.</p>
            </div>
          </div>
        )}

        {/* Quote Details */}
        <Card className="shadow-lg border-slate-200 mb-6">
          <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl mb-2">Proposed Services</CardTitle>
                <p className="text-sm text-slate-600">
                  Prepared for: <span className="font-semibold text-slate-800">{prospect?.company_name}</span>
                </p>
              </div>
              {quote.valid_until && (
                <div className="text-right">
                  <p className="text-xs text-slate-600">Valid Until</p>
                  <p className="font-semibold text-slate-800">
                    {new Date(quote.valid_until).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Services Table */}
            <div className="mb-8">
              <h3 className="font-semibold text-slate-800 mb-4 text-lg">Services Included</h3>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Service</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Price/User</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Users</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.services.map((item, index) => (
                      <tr key={index} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-4 px-4 text-slate-800 font-medium">{item.service_name}</td>
                        <td className="py-4 px-4 text-right text-slate-600">
                          £{item.base_price.toFixed(2)}
                        </td>
                        <td className="py-4 px-4 text-center text-slate-600">
                          {item.quantity}
                        </td>
                        <td className="py-4 px-4 text-right font-medium text-slate-800">
                          £{item.total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-6 border border-slate-200">
              <div className="space-y-3">
                <div className="flex justify-between text-slate-700">
                  <span>Monthly Subtotal</span>
                  <span className="font-medium">£{quote.subtotal.toFixed(2)}</span>
                </div>
                
                {quote.discount_percent > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Discount ({quote.discount_percent}%)</span>
                    <span className="font-medium">-£{quote.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="pt-4 border-t border-slate-300 flex justify-between items-center">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total per Month</p>
                    <p className="text-3xl font-bold text-emerald-600">£{quote.total.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600 mb-1">Annual Total</p>
                    <p className="text-xl font-semibold text-slate-700">£{(quote.total * 12).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">Additional Information</h4>
                <p className="text-sm text-blue-800 whitespace-pre-wrap">{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canTakeAction && (
          <Card className="shadow-lg border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Ready to Get Started?</h3>
                <p className="text-slate-600">
                  Accept this quote to begin your service, or let us know if you'd like to discuss alternatives.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleAcceptQuote}
                  disabled={accepting || rejecting}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-8 py-3 text-lg"
                >
                  {accepting ? (
                    'Processing...'
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Accept Quote
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleRejectQuote}
                  disabled={accepting || rejecting}
                  variant="outline"
                  className="border-slate-300 px-8 py-3 text-lg"
                >
                  {rejecting ? (
                    'Processing...'
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 mr-2" />
                      Decline Quote
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Contracts Section - Show when quote is accepted */}
        {quote.status === 'accepted' && (
          <Card className="shadow-lg border-emerald-200 bg-emerald-50/30 mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Service Contracts
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">
                Please review and sign the service agreements below to complete the process
              </p>
            </CardHeader>
            <CardContent>
              {loadingContracts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                  <span className="ml-3 text-slate-600">Loading contracts...</span>
                </div>
              ) : contracts.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    📋 Your service contracts are being generated. Please refresh this page in a moment, or check your email for the signing links.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contracts.map((contract) => (
                    <div key={contract.id} className="bg-white border border-slate-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 mb-1">{contract.title}</h4>
                          <p className="text-sm text-slate-600 mb-3">
                            Status: <span className={`font-medium ${contract.status === 'signed' ? 'text-green-600' : 'text-amber-600'}`}>
                              {contract.status === 'signed' ? '✓ Signed' : 'Awaiting Your Signature'}
                            </span>
                          </p>
                          {contract.value && (
                            <p className="text-sm text-slate-500">
                              Contract Value: {contract.currency} {contract.value.toFixed(2)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          {contract.signing_token && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`/contracts/sign/${contract.signing_token}`, '_blank')}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50 whitespace-nowrap"
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                View Contract
                              </Button>
                              {contract.status !== 'signed' && (
                                <Button
                                  size="sm"
                                  onClick={() => window.open(`/contracts/sign/${contract.signing_token}`, '_blank')}
                                  className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
                                >
                                  <PenTool className="w-4 h-4 mr-1" />
                                  Sign Now
                                </Button>
                              )}
                            </>
                          )}
                          {!contract.signing_token && (
                            <p className="text-xs text-slate-500">Link will be sent via email</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {contracts.some(c => c.status !== 'signed') && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                      <p className="text-sm text-amber-800">
                        <strong>⚠️ Important:</strong> Please sign all contracts to activate your services. You will also receive email links for each contract.
                      </p>
                    </div>
                  )}
                  
                  {contracts.every(c => c.status === 'signed') && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                      <p className="text-sm text-green-800">
                        <strong>✓ All Done!</strong> All contracts have been signed. Our team will be in touch shortly to begin your service setup.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}


        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-slate-600">
          <p>Questions about this quote?</p>
          <p className="mt-1">
            Contact us at{' '}
            <a href={`mailto:${prospect?.contact_email || 'sales@example.com'}`} className="text-emerald-600 hover:underline font-medium">
              {prospect?.contact_email || 'sales@example.com'}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PublicQuoteView;
